import * as IF from "infrontjs";

// Core
import { View } from "./View";
import { Api } from "./Api";
import { Storage } from "./Storage";

// i18n
import {
    DEFAULT_LANGUAGE, DICTIONARIES, registerI18n, resolveLanguage, _lcs
} from "../i18n";

// Config (single global region — no region logic)
import config from "../config.json";

// States
import { LoginState  } from "../states/auth/LoginState";
import { LogoutState } from "../states/auth/LogoutState";
import { ForgotPasswordState } from "../states/auth/ForgotPasswordState";
import { PasswordResetState  } from "../states/auth/PasswordResetState";
import { TokenState  } from "../states/auth/TokenState";
import { IndexState  } from "../states/index/IndexState";
import { AnalyticsState } from "../states/analytics/AnalyticsState";
import { AdminUser         } from "../states/admin/AdminUser";
import { Logs              } from "../states/admin/Logs";
import { AiCosts           } from "../states/admin/AiCosts";
import { AiCostsDetail     } from "../states/admin/AiCostsDetail";
import { ClientUser        } from "../states/client/ClientUser";
import { GuestAccount      } from "../states/guest/GuestAccount";
import { GuestAccountDetail } from "../states/guest/GuestAccountDetail";
import { PushBroadcast      } from "../states/guest/PushBroadcast";
import { ProfileState      } from "../states/account/ProfileState";
import { SettingsState     } from "../states/account/SettingsState";
import { AccommodationList } from "../states/accommodation/AccommodationList";
import { PlatformEventList } from "../states/event/PlatformEventList";
import { EventSourceList   } from "../states/event/EventSourceList";
import { GastroList        } from "../states/place/GastroList";
import { PoiList           } from "../states/place/PoiList";
import { TourList          } from "../states/tour/TourList";
// Unknown route (old / mistyped link)
import { NotFoundState     } from "../states/notfound/NotFoundState";

export class App extends IF.App
{
    constructor()
    {
        // Hash-mode routing (`#/sources`) — same reason as the guest-app, see
        // its core/App.js for the full account: the console is static files in a
        // GCS bucket behind the LB, a bucket has no SPA fallback, and a hard load
        // of /sources answered 404 (with index.html as the body, so the page
        // worked and only the status was wrong). The LB cannot rewrite it —
        // urlRewrite is a backend-service feature and these are backend buckets;
        // both rewrite forms were tested live and did nothing.
        super( null, { router : { mode : 'hash' } } );

        // null = logged out. resolveSession() fills this from a stored token on
        // boot; LoginState fills it after a successful sign-in.
        this.user = null;
        this.session = null;

        // App config (api endpoint, version) — read via this.config.get(...).
        this.config = new IF.PathObject( config );

        // One bundle for every environment: on production hosts
        // (admin.belturo.com) the API endpoint and client-admin URL template
        // switch to the live values; localhost keeps the local dev values
        // from config.json.
        const host = ( window.location.hostname || "" ).toLowerCase();
        if ( host === "belturo.app" || host.endsWith( ".belturo.app" ) || host.endsWith( ".belturo.com" ) )
        {
            if ( this.config.get( "apiLive" ) )
            {
                this.config.set( "api", this.config.get( "apiLive" ) );
            }
            if ( this.config.get( "clientAdminUrlLive" ) )
            {
                this.config.set( "clientAdminUrl", this.config.get( "clientAdminUrlLive" ) );
            }
        }

        // Persistent session-token store (localStorage).
        this.storage = new Storage();

        // REST client for the core-api.
        this.api = new Api( this );

        // Use our custom View (renders the <app-layout> shell, auth/in-app switch).
        this.view = new View( this );
    }

    /**
     * Configure InfrontJS's I18n and load the dictionaries.
     *
     * Overridden because IF.App's own initL18n() constructs `new I18n( this )`
     * with no options — the `l18n` block of the App config is never read — so
     * the default language, the fallback and the browser detection can only be
     * set from here.
     *
     * Called from IF.App's constructor (i.e. from super()), before this class's
     * own constructor body has run: nothing below may touch this.storage or any
     * other field assigned there.
     */
    initL18n()
    {
        this.l18n = new IF.I18n( this, {
            defaultLanguage       : DEFAULT_LANGUAGE,
            fallbackLanguage      : DEFAULT_LANGUAGE,
            // Resolved against the languages we ship, below. I18n's own
            // detection accepts any valid ISO code, which would leave a French
            // browser in "fr" with no dictionary behind it.
            detectBrowserLanguage : false
        } );

        Object.keys( DICTIONARIES ).forEach(
            ( code ) => this.l18n.setDictionary( code, DICTIONARIES[ code ] )
        );

        // Hand the instance to the i18n module so the plain JS that renders
        // outside a template (label maps, grid columns, toasts) can translate
        // too — templates get _lcs injected by IF.View.
        registerI18n( this.l18n );

        // Storage is stateless (every call reads localStorage), so a local one
        // is the same store as the this.storage created a moment later.
        this.setCurrentLanguage( resolveLanguage( new Storage().loadLanguage() ) );
    }

    /**
     * Apply a language to the running app: I18n plus the document's `lang`
     * attribute, which is what screen readers and the browser's own hyphenation
     * go by.
     */
    setCurrentLanguage( langCode )
    {
        this.l18n.setCurrentLanguage( langCode );
        if ( document && document.documentElement )
        {
            document.documentElement.setAttribute( "lang", this.l18n.getCurrentLanguage() );
        }
    }

    /**
     * Switch the console's language for good: persist the choice and reload.
     *
     * A reload rather than a re-render. The shell, the sidebar, the topbar and
     * the active screen each rendered their text once, and a good deal of it
     * (GridJS column headers, ECharts axis labels, an open modal) lives inside
     * third-party widgets that would each have to be torn down and rebuilt. The
     * route is in the hash and the session in localStorage, so the operator
     * lands back on the same screen.
     */
    switchLanguage( langCode )
    {
        const next = resolveLanguage( langCode );
        if ( next === this.l18n.getCurrentLanguage() )
        {
            return;
        }
        this.storage.saveLanguage( next );
        window.location.reload();
    }

    async init()
    {
        this.stateManager.add(
            LoginState,
            LogoutState,
            ForgotPasswordState,
            PasswordResetState,
            TokenState,
            IndexState,
            AnalyticsState,
            AdminUser,
            Logs,
            // More specific route first ("/admin/ai-costs/:organisationId" vs "/admin/ai-costs").
            AiCostsDetail,
            AiCosts,
            ClientUser,
            // More specific route first ("/guest/users/:userId" vs "/guest/users").
            GuestAccountDetail,
            GuestAccount,
            PushBroadcast,
            ProfileState,
            SettingsState,
            AccommodationList,
            EventSourceList,
            PlatformEventList,
            GastroList,
            PoiList,
            TourList,
            NotFoundState
        );
        // Instead of the framework splash, which wipes the shell (see the state).
        this.stateManager.stateNotFoundClass = NotFoundState;

        // Restore a persisted session (if any) before the shell renders.
        await this.resolveSession();

        // View.init() renders the layout and switches to auth/in-app based on user.
        // Called here (not by IF.App) — mirrors the pdvision-client lifecycle.
        this.view.init();
    }

    async run( route = null )
    {
        await this.init();
        await super.run( route );
    }

    /**
     * Tear down the current session: clear the local token, drop the API auth
     * header, forget the user, then tell the backend to delete the session.
     * Always resolves — the DELETE route returns OK regardless.
     */
    async destroySession()
    {
        this.storage.deleteSessionToken();
        this.api.deleteSessionToken();
        this.user = null;
        this.session = null;
        this.dispatchEvent( new CustomEvent( 'auth-user-logout' ) );
        const { result } = await this.api.delete( '/auth/session' );
        this.view.switchToAuth();
        return result;
    }

    /**
     * Restore a session from the stored token: validate it against the backend,
     * and on success populate this.user / this.session and enter the in-app
     * shell. Invalid/expired tokens are cleared and the app stays on auth.
     */
    async resolveSession()
    {
        this.user = null;
        this.session = null;

        const localSessionToken = this.storage.loadSessionToken();
        if ( !localSessionToken )
        {
            return;
        }

        let res = null;
        try
        {
            const { result } = await this.api.get( '/auth/session/' + localSessionToken );
            res = result;
        }
        catch ( e )
        {
            console.error( e );
        }

        if ( res && 200 === res.status )
        {
            const sessionData = res.data;
            if ( sessionData && sessionData.hasOwnProperty( 'user' ) )
            {
                this.api.setSessionToken( localSessionToken );
                this.session = sessionData;
                this.user = this.toLayoutUser( sessionData.user );
                this.dispatchEvent( new CustomEvent( 'auth-user-login' ) );
                this.view.switchToInApp();
            }
        }
        else
        {
            this.storage.deleteSessionToken();
            this.view.switchToAuth();
        }
    }

    /**
     * Map a core-api user payload onto the plain object the <app-layout> shell
     * renders (name / email / role / initials). saas-admin is admin-only, so the
     * displayed role is a fixed label.
     */
    toLayoutUser( apiUser )
    {
        const name = ( apiUser?.data?.name || apiUser?.email || "" ).trim();
        return {
            id       : apiUser?.id || null,
            name     : name || _lcs( "Operator" ),
            email    : apiUser?.email || "",
            // Display label for the saas-admin console (admin-only).
            role     : _lcs( "Plattform-Operator" ),
            roles    : Array.isArray( apiUser?.role ) ? apiUser.role : [],
            initials : this._initialsFrom( name || apiUser?.email || "?" )
        };
    }

    _initialsFrom( str )
    {
        const parts = String( str ).trim().split( /\s+/ ).filter( Boolean );
        let initials = "?";
        if ( parts.length >= 2 )
        {
            initials = parts[ 0 ][ 0 ] + parts[ 1 ][ 0 ];
        }
        else if ( parts.length === 1 )
        {
            initials = parts[ 0 ].slice( 0, 2 );
        }
        return initials.toUpperCase();
    }
};
